import type {
  CompleteBillingBatchItem,
  ConsentedDocument,
} from '../types/subscription.type';

/**
 * 자동결제(빌링) 카드등록 → 리다이렉트 간 "의도(intent)" 보존용 sessionStorage.
 *
 * 카드등록 성공 리다이렉트엔 authKey+customerKey 만 실리므로, "이 카드등록으로 무엇을
 * 할지"(구독 시작 / 카드 변경 / 재활성화)를 여기에 저장해 빌링 성공 페이지에서 분기한다.
 * (store·mypage·기업콘솔 = 의도 저장, features/payments 빌링 성공 페이지 = 소비·분기)
 *
 * web 과 console-web 이 **같은 origin·같은 키**를 쓴다. 앱마다 복사하면 한쪽만 필드를
 * 늘렸을 때 다른 앱이 쓴 intent 를 조용히 반쪽으로 읽는다.
 */
export const BILLING_INTENT_KEY = 'billing:intent';

export type BillingIntent =
  | {
      purpose: 'subscribe';
      planId: string;
      /** 기업 구독이면 orgId. 개인은 생략. */
      orgId?: string;
      seatCount?: number;
      /** 약정형 플랜이면 선택한 약정 개월(§7.7). 무약정/개인은 생략. */
      commitmentMonths?: number;
      /** 약정 조건 명시 동의 여부(약정 선택 시 true 필수). */
      agreedToTerms?: boolean;
      /** 약정형 플랜을 무약정(정가)으로 구매하겠다는 명시 의사. 서버가 값 누락과 구분한다. */
      commitmentOptOut?: boolean;
      /**
       * 구매 화면에서 확인한 약관 스냅샷 `[{key, version}]`.
       * sessionStorage 는 사용자가 조작할 수 있으므로 서버가 재검증한다 — 여기 값은
       * 리다이렉트 왕복 동안 의도를 보존하기 위한 것이지 신뢰 근거가 아니다.
       */
      consentedDocuments?: ConsentedDocument[];
    }
  | {
      /** 단품 다중 구매. 카드등록 1회로 items 마다 구독을 만든다. */
      purpose: 'subscribe-batch';
      orgId?: string;
      /** planName 은 결과 화면 표시용이다 — 서버는 planId 만 본다. */
      items: (CompleteBillingBatchItem & { planName: string })[];
      consentedDocuments?: ConsentedDocument[];
    }
  | { purpose: 'change-card'; subscriptionId: string; returnTo?: string }
  | { purpose: 'reactivate'; subscriptionId: string; returnTo?: string };

export function setBillingIntent(intent: BillingIntent): void {
  sessionStorage.setItem(BILLING_INTENT_KEY, JSON.stringify(intent));
}

export function readBillingIntent(): BillingIntent | null {
  const raw = sessionStorage.getItem(BILLING_INTENT_KEY);
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as BillingIntent;
    return v && typeof v === 'object' && 'purpose' in v ? v : null;
  } catch {
    return null;
  }
}

export function clearBillingIntent(): void {
  sessionStorage.removeItem(BILLING_INTENT_KEY);
}
